/**
 * Designed  & Developed by Faisal Nadaf
 * Contact: faisalfnadaf@gmail.com
 *
 * @format
 */

import { useEffect, useId, useRef, type FC } from "react";
import { Link } from "react-router-dom";
import { motion, type Variants } from "framer-motion";
import { ArrowRight } from "lucide-react";
import gsap from "gsap";

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

export type Card3DVariant = "amber" | "indigo" | "teal" | "rose" | "emerald";

export interface Card3DProps {
	title: string;
	description?: string;
	image: string;
	href?: string;
	variant?: Card3DVariant;
	/** Big italic wordmark in the top-left (decorative). Default "POSITRON". */
	eyebrow?: string;
	/** Big italic wordmark in the bottom-right (decorative). Default "THERMAL". */
	subEyebrow?: string;
	/** Label inside the call-to-action pill. Default "Explore". */
	ctaLabel?: string;
	/**
	 * Tailwind size/scale utilities for the product photo box. Landscape
	 * artwork (e.g. dry coolers) renders far wider than tall inside the default
	 * square box, so it needs a smaller override to stay within the card.
	 */
	imageSizeClassName?: string;
	/**
	 * Tailwind placement utilities for the product photo. Override when the
	 * artwork sits off-centre inside its own canvas and needs nudging back.
	 */
	imagePositionClassName?: string;
	/** Click handler — overrides anchor navigation when provided. */
	onClick?: () => void;
}

/* ------------------------------------------------------------------ */
/*  Variant accent tokens (one source of truth for hover accents)      */
/* ------------------------------------------------------------------ */

/** Default photo box — deliberately larger than the card so art bleeds out. */
const DEFAULT_IMAGE_SIZE = "h-88 w-88 scale-110 sm:h-136 sm:w-136";

/** Default photo placement — centred horizontally, slightly below mid-card. */
const DEFAULT_IMAGE_POSITION = "left-1/2 top-[60%] -translate-x-1/2 -translate-y-1/2";

const ACCENT: Record<Card3DVariant, string> = {
	amber: "#3b0092",
	indigo: "#3b0092",
	teal: "#3b0092",
	rose: "#3b0092",
	emerald: "#3b0092",
};

/* ------------------------------------------------------------------ */
/*  Framer Motion variants for the inner pieces                        */
/* ------------------------------------------------------------------ */

const nameVariants: Variants = {
	rest: { y: 0, opacity: 1, z: 75 },
	hover: { y: 0, opacity: 1, z: 95, transition: { duration: 0.5 } },
};

const buyVariants: Variants = {
	rest: { y: 0, opacity: 0, z: 75 },
	hover: { y: -30, opacity: 1, z: 75, transition: { duration: 0.5 } },
};

const productVariants: Variants = {
	rest: { z: 0, rotate: 0 },
	hover: { z: 100, rotate: 0, transition: { duration: 0.5 } },
};

const labelFrontVariants: Variants = {
	rest: { opacity: 0 },
	hover: { opacity: 0.05, transition: { duration: 0.5 } },
};

/* ------------------------------------------------------------------ */
/*  Card3D — 3-D tilt product card                                     */
/* ------------------------------------------------------------------ */

const Card3D: FC<Card3DProps> = ({
	title,
	description,
	image,
	href,
	variant = "amber",
	eyebrow = "POSITRON",
	subEyebrow = "THERMAL",
	ctaLabel = "Explore",
	imageSizeClassName = DEFAULT_IMAGE_SIZE,
	imagePositionClassName = DEFAULT_IMAGE_POSITION,
	onClick,
}) => {
	const tiltRef = useRef<HTMLDivElement>(null);
	const accent = ACCENT[variant];
	// Unique id per card so the inline gradient styles stay scoped.
	const reactId = useId().replace(/[^a-zA-Z0-9]/g, "");
	const scopeClass = `c3d-${reactId}`;

	/* GSAP-powered 3-D tilt on mouse-move */
	useEffect(() => {
		const el = tiltRef.current;
		if (!el) return;

		const ease = "cubic-bezier(.03,.98,.52,.99)";
		const MAX = 15;

		const handleMove = (e: MouseEvent) => {
			const rect = el.getBoundingClientRect();
			const px = (e.clientX - rect.left) / rect.width;
			const py = (e.clientY - rect.top) / rect.height;
			const rotateY = (px - 0.5) * 2 * MAX;
			const rotateX = (0.5 - py) * 2 * MAX;

			el.style.setProperty("--glow-x", `${px * 100}%`);
			el.style.setProperty("--glow-y", `${py * 100}%`);
			el.style.setProperty("--glow-intensity", "1");

			gsap.to(el, {
				rotateX,
				rotateY,
				scale: 1.05,
				duration: 0.3,
				ease,
				transformPerspective: 1000,
				transformOrigin: "center",
			});
		};

		const handleLeave = () => {
			el.style.setProperty("--glow-intensity", "0");
			gsap.to(el, {
				rotateX: 0,
				rotateY: 0,
				scale: 1,
				duration: 0.6,
				ease,
			});
		};

		el.addEventListener("mousemove", handleMove);
		el.addEventListener("mouseleave", handleLeave);
		return () => {
			el.removeEventListener("mousemove", handleMove);
			el.removeEventListener("mouseleave", handleLeave);
		};
	}, []);

	const cardInner = (
		<div
			ref={tiltRef}
			// A rigid `w-90` overflowed every phone viewport, but `w-full` cannot be
			// used here: every child of `.c3d-box` is absolutely positioned, so the
			// card's min-content width is 0 and a percentage width collapses it to
			// a sliver inside the parent flex row. The width is therefore resolved
			// against the viewport, which is always definite — `3rem` matches the
			// grid's horizontal padding plus this card's own margins. From `sm` up
			// the original 360x440 geometry is restored exactly (no `max-w`, which
			// would otherwise clamp `w-90` down to 288px).
			className={`${scopeClass} relative m-2 h-100 w-[min(22rem,calc(100vw-3rem))] sm:m-4 sm:h-110 sm:w-90`}
			style={{ transformStyle: "preserve-3d" }}>
			{/* Inline scoped styles — preserves the original gradient + mask
			    border without leaking globally. */}
			<style>{`
				.${scopeClass} {
					--glow-x: 50%;
					--glow-y: 50%;
					--glow-intensity: 0;
				}
				.${scopeClass} .c3d-box {
					background: radial-gradient(ellipse at right top,
						${accent}ed 0%, #232323 47%, #232323 100%);
				}
				.${scopeClass} .c3d-box::before {
					content: "";
					position: absolute;
					inset: 0;
					border-radius: 1.25rem;
					padding: 0.155rem;
					z-index: -1;
					background: linear-gradient(45deg,
						#232323, #232323, #232323, #232323, ${accent});
					-webkit-mask:
						linear-gradient(#fff 0 0) content-box,
						linear-gradient(#fff 0 0);
					-webkit-mask-composite: xor;
					        mask-composite: exclude;
					pointer-events: none;
				}
				.${scopeClass} .c3d-spotlight {
					background: radial-gradient(
						circle at var(--glow-x) var(--glow-y),
						rgba(240, 154, 56, calc(var(--glow-intensity) * 0.28)),
						transparent 60%
					);
					opacity: var(--glow-intensity);
					transition: opacity 0.25s ease;
				}
				.${scopeClass} .c3d-border-glow {
					padding: 1.5px;
					background: radial-gradient(
						circle at var(--glow-x) var(--glow-y),
						rgba(240, 154, 56, calc(var(--glow-intensity) * 0.9)),
						transparent 55%
					);
					-webkit-mask:
						linear-gradient(#000 0 0) content-box,
						linear-gradient(#000 0 0);
					-webkit-mask-composite: xor;
					        mask-composite: exclude;
					opacity: var(--glow-intensity);
					transition: opacity 0.25s ease;
				}
				.${scopeClass} .c3d-halo {
					background: radial-gradient(
						circle at var(--glow-x) var(--glow-y),
						rgba(240, 154, 56, calc(var(--glow-intensity) * 0.45)),
						transparent 60%
					);
					filter: blur(18px);
					opacity: var(--glow-intensity);
					transition: opacity 0.3s ease;
				}
			`}</style>

			<motion.div
				initial="rest"
				animate="rest"
				whileHover="hover"
				className="c3d-box relative h-full w-full rounded-[20px]"
				style={{ transformStyle: "preserve-3d" }}>
				{/* Decorative wordmarks — sized in `em` so they scale with the card
				    instead of spilling past its rounded edge on narrow screens. */}
				<motion.span
					variants={labelFrontVariants}
					className="pointer-events-none absolute left-5 top-5 select-none italic font-black text-[3.4em] sm:text-[5em]"
					style={{
						color: "#ffffff",
						lineHeight: 1,
						letterSpacing: "-0.02em",
					}}>
					{eyebrow}
				</motion.span>
				<motion.span
					variants={labelFrontVariants}
					className="pointer-events-none absolute bottom-5 right-5 select-none italic font-black text-[2.5em] sm:text-[3.6em]"
					style={{
						color: "#ffffff",
						lineHeight: 1,
						letterSpacing: "-0.02em",
					}}>
					{subEyebrow}
				</motion.span>

				{/* Title block (slides down + fades in on hover) */}
				<motion.div
					variants={nameVariants}
					className="absolute left-0 top-0 z-10 w-full px-4 pt-10 text-center sm:px-5 sm:pt-14"
					style={{ transformStyle: "preserve-3d" }}>
					<h3 className="text-lg font-semibold leading-tight text-white drop-shadow-[0_2px_8px_rgba(0,0,0,0.7)] sm:text-xl">
						{title}
					</h3>
					{description && (
						<p className="mx-auto mt-2 max-w-65 text-xs leading-snug text-white/85 drop-shadow-[0_1px_4px_rgba(0,0,0,0.7)] sm:text-[13px]">
							{description}
						</p>
					)}
				</motion.div>

				{/* Product photo */}
				<motion.img
					src={image}
					alt={title}
					loading="lazy"
					decoding="async"
					variants={productVariants}
					// Sized so the intentional bleed past the card edge stays within the
					// grid's row gap instead of spilling onto the next stacked card.
					className={`absolute z-11 object-contain drop-shadow-[0_24px_30px_rgba(0,0,0,0.55)] ${imagePositionClassName} ${imageSizeClassName}`}
					style={{ transformStyle: "preserve-3d" }}
					onError={(e) => {
						(e.currentTarget as HTMLImageElement).src = "/noimg.png";
					}}
				/>

				{/* Mouse-tracked orange spotlight overlay */}
				<div className="c3d-spotlight pointer-events-none absolute inset-0 z-20 rounded-[20px]" />

				{/* Mouse-tracked orange border glow */}
				<div className="c3d-border-glow pointer-events-none absolute inset-0 z-21 rounded-[20px]" />
			</motion.div>

			{/* Outer blurred halo bleed (behind the card) */}
			<div className="c3d-halo pointer-events-none absolute -inset-2 z-[-1] rounded-3xl" />
		</div>
	);

	if (onClick) {
		return (
			<button
				type="button"
				onClick={onClick}
				className="block bg-transparent p-0 [perspective:1200px]"
				aria-label={title}>
				{cardInner}
			</button>
		);
	}

	if (href) {
		// Internal routes navigate client-side (no full page reload); external
		// URLs fall back to a plain anchor.
		const isInternal = href.startsWith("/") && !href.startsWith("//");
		if (isInternal) {
			return (
				<Link
					to={href}
					className="block [perspective:1200px]"
					aria-label={title}>
					{cardInner}
				</Link>
			);
		}
		return (
			<a
				href={href}
				className="block [perspective:1200px]"
				aria-label={title}>
				{cardInner}
			</a>
		);
	}

	return <div className="[perspective:1200px]">{cardInner}</div>;
};

export default Card3D;
